import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
  IGetCoursePlanListParams,
  IBackNewCoursePlanningDto,
  ICoursePlan,
  INewPlanFormDto,
  IUpdatePlanDto,
  ICoursePlanRaw,
  IPostNewPlanDto,
} from "../interfaces/course-planning";
import { Observable, map } from "rxjs";
import { ResponseDataInterfaces } from "src/app/models/Interfaces/ResponseDataInterfaces";
import {
  IHeadDevReassignmentRes,
  IInstructorReassignmentPreviewRes,
  IInstructorReassignmentRes,
  IReassignmentReq,
} from "../interfaces/reassignment";
import { getMonthName } from "src/app/shared/utils/utils";
import { environment } from "src/environments/environment";
import {
  IBackNewCourseDto,
  NewCourseDto,
} from "../interfaces/newCourse.interface";
import {
  IIncreasedPlanValues,
  IPostDuplicatePlanBody,
  IPostIncrementPlanValuesBody,
  IPostIncrementPlanValuesResponse,
} from "../interfaces/duplicatePlanning";

@Injectable({
  providedIn: "root",
})
export class CoursePlanningService {
  private readonly coursePlanURl = `${environment.apiUrlCap}/api/planning/planeacionCurso`;
  private readonly headDevReassignmentUrl = `${environment.apiUrlCap}/api/reallocation`;
  private readonly newCourseRequest = `${environment.apiUrlCap}/api/courseCreationRequest`;
  constructor(private httpClient: HttpClient) {}

  private newCourseRequestToBackDto(d: NewCourseDto): IBackNewCourseDto {
    return {
      tipoDeAsistencia: d.courseType,
      descripcion: d.description,
      temas: d.topics,
      registroMinimo: d.minimumRegisters,
      registroMaximo: d.maximumRegisters,
      idiomas: d.availableLanguages,
      tituloCapacitacion: d.courseTitle,
      horasCapacitacion: Number(d.courseHours),
      academia: d.academy.nombre,
      escuela: d.school.nombre,
      factor: d.factor.nombre,
      grupo: d.group.nombre,
      competencia: d.competence.nombre,
      contactoCapacitacion: d.courseContact,
      destinatarios: d.destinatarios,
      idAcademia: d.academy.id,
      idEscuela: d.school.id,
      idFactor: d.factor.id,
      idGrupo: d.group.id,
      idCompetencia: d.competence.id,
    };
  }
  private BackDtoToICoursePlan(
    dataRaw: IBackNewCoursePlanningDto[]
  ): ICoursePlan[] {
    const array = dataRaw.map(data => {
      const newICourseDto: ICoursePlan = {
        idPlaneacionCurso: data.idPlaneacionCurso,
        numeroPersona: data.numeroPersona,
        fkIdUsuario: data.fkIdUsuario,
        nombreInstructor: data.nombreInstructor,
        year: data.anios,
        numeroMes: data.fkIdMes,
        nombreMes: data.mes.descripcion ?? getMonthName(data.fkIdMes),
        tipoAsistencia: data.tipoAsistencia
          ? data.tipoAsistencia.nombre
          : `Not Implemented - ${data.fkTipoAsistencia}`,
        nombreCurso: data.nombre ?? `Not Implemented - ${data.nombre}`,
        areaFuncional: data.area ?? "Not Implemented",
        numeroSesiones: data.numeroDeSesiones,
        colaboradoresPorSesion: data.colaboradoresPorSesion,
        totalColaboradores: data.totalColaboradores,
        horasPorSesion: data.horasSesion,
        totalHoras: data.totalHoras ?? 9999,
        sede: data.sede ?? `Not Populated - ${data.fkIdSede}`,
        region: data.region ?? `Not Populated - ${data.fkIdRegion}`,
        organizacion:
          data.organizacion ?? `Not Populated - ${data.fkIdOrganizacion}`,
        tipoCurso: data.tipoCurso ?? `Not Populated ${data.fkIdTipoCurso}`,
        jefeDesarrollo: data.jefeDesarrollo,
        liderNacional: data.liderNacional,
        fechaAlta: new Date(data.fechaAlta),
        pais: data.pais,
        academia: data.academia,
        escuela: data.escuela,
        fkEventNumber: data.fkEventNumber,
      };
      return newICourseDto;
    });
    return array;
  }

  private convertToBackEditCoursePlanningDto(
    newPlanDto: INewPlanFormDto,
    userId: number
  ): IUpdatePlanDto {
    return {
      ...newPlanDto.monthsArray[0],
      idPlaneacionCurso: newPlanDto.idPlaneacionCurso,
      nombre: newPlanDto.nombreCurso.Title,
      descripcion: newPlanDto.nombreCurso.Description,
      fkObjectId: newPlanDto.nombreCurso.ObjectId,
      fkIdOrganizacion: Number(newPlanDto.organizacion),
      fkIdPais: Number(newPlanDto.pais),
      fkIdTipoAsistencia: Number(newPlanDto.tipoAsistencia),
      fkIdArea: Number(newPlanDto.areaFuncional),
      jefeDesarrollo: newPlanDto.jefeDesarrollo,
      fkIdSubArea: Number(newPlanDto.subArea),
      fkIdSede: Number(newPlanDto.sede), //Catalog
      fkIdRegion: newPlanDto.region, // Catalo
      fechaAlta: newPlanDto.fechaAlta, // Needed on Front
      fkIdAcademia: Number(newPlanDto.academia),
      fkIdEscuela: Number(newPlanDto.escuela),
      fkIdTipoCurso: Number(newPlanDto.tipoCurso),
      fkIdTipoCapacitacion: newPlanDto.tipoCapacitacion,
      numeroPersona: newPlanDto.personNumber,
      fkIdUsuario: userId,
      fkIdAnio: newPlanDto.anio,
      fkEventNumber: newPlanDto.fkEventNumber,

      totalParticipantesOriginal: newPlanDto.totalParticipantesOriginal,
      totalSesionesOriginal: newPlanDto.totalSesionesOriginal,
    };
  }

  private convertToBackCreateCoursePlanningDto(
    newPlanDto: INewPlanFormDto,
    userId: number
  ): IPostNewPlanDto {
    return {
      idPlaneacionCurso: newPlanDto.idPlaneacionCurso,
      nombre: newPlanDto.nombreCurso.Title,
      fkObjectId: newPlanDto.nombreCurso.ObjectId,
      descripcion: newPlanDto.nombreCurso.Description,
      fkIdOrganizacion: Number(newPlanDto.organizacion),
      fkIdPais: Number(newPlanDto.pais),
      fkIdTipoAsistencia: Number(newPlanDto.tipoAsistencia),
      fkIdArea: Number(newPlanDto.areaFuncional),
      jefeDesarrollo: newPlanDto.jefeDesarrollo,
      fkIdSubArea: Number(newPlanDto.subArea),
      fkIdSede: Number(newPlanDto.sede), //Catalog
      fkIdRegion: newPlanDto.region, // Catalo
      fechaAlta: new Date(), // Needed on Front
      fkIdAcademia: Number(newPlanDto.academia),
      fkIdEscuela: Number(newPlanDto.escuela),
      fkIdTipoCurso: Number(newPlanDto.tipoCurso),
      fkIdTipoCapacitacion: newPlanDto.tipoCapacitacion,
      numeroPersona: newPlanDto.personNumber,
      fkIdUsuario: userId,
      fkIdAnio: newPlanDto.anio,
      infoMonths: newPlanDto.monthsArray,
    };
  }

  public requestNewCourse(newCourseDto: NewCourseDto) {
    const payload = this.newCourseRequestToBackDto(newCourseDto);
    const url = `${this.newCourseRequest}`;
    return this.httpClient.post(url, payload);
  }

  public getCoursePlansList(searchParams: IGetCoursePlanListParams) {
    const url = `${this.coursePlanURl}`;
    let params = new HttpParams();
    for (const key in searchParams) {
      if (Object.hasOwn(searchParams, key)) {
        const value = searchParams[key as keyof IGetCoursePlanListParams];
        if (
          value === undefined ||
          value === null ||
          value === "" ||
          (typeof value === "number" && value === 0)
          // value !== undefined &&
          // value !== null &&
          // value !== 0 &&
          // value != ""
        ) {
          continue;
        }
        {
          if (Array.isArray(value)) {
            value.forEach(item => {
              params = params.append(key, item);
            });
          } else {
            params = params.append(key, value.toString());
          }
        }
      }
    }
    return this.httpClient
      .get<ResponseDataInterfaces<IBackNewCoursePlanningDto[]>>(url, {
        params,
      })
      .pipe(
        map(({ data, pagination }) => ({
          data: this.BackDtoToICoursePlan(data),
          pagination,
        }))
      );
  }

  public getCoursePlanById(
    personNumber: string,
    coursePlanId: string
  ): Observable<IBackNewCoursePlanningDto> {
    const url = `${this.coursePlanURl}`;
    const params = new HttpParams()
      .set("numeroPersona", personNumber)
      .set("planningId", coursePlanId);
    return this.httpClient
      .get<ResponseDataInterfaces<IBackNewCoursePlanningDto[]>>(url, { params })
      .pipe(map(r => r.data[0]));
  }

  public getOneCoursePlanById(
    coursePlanId: string
  ): Observable<ICoursePlanRaw> {
    const url = `${this.coursePlanURl}`;
    return this.httpClient
      .get<ResponseDataInterfaces<ICoursePlanRaw>>(`${url}/${coursePlanId}`)
      .pipe(map(r => r.data));
  }

  public NewPlanningEvent(newPlanDto: INewPlanFormDto, userId: number) {
    const url = `${this.coursePlanURl}`;
    const payload: IPostNewPlanDto = this.convertToBackCreateCoursePlanningDto(
      newPlanDto,
      userId
    );
    return this.httpClient.post(url, payload);
  }
  public EditPlanningEvent(newPlanDto: INewPlanFormDto, userId: number) {
    const url = `${this.coursePlanURl}`;
    const payload: IUpdatePlanDto = this.convertToBackEditCoursePlanningDto(
      newPlanDto,
      userId
    );
    return this.httpClient.put(url, payload);
  }

  public reassignPlanInstructor(reassignmentData: IReassignmentReq) {
    const url = `${this.coursePlanURl}/v2/updateOwner`;
    return this.httpClient
      .put<ResponseDataInterfaces<IInstructorReassignmentRes>>(
        url,
        reassignmentData
      )
      .pipe(map(({ data }) => data));
  }

  public reassignPlanInstructorPreview(userId: number) {
    const url = `${this.coursePlanURl}/v2/updateOwner/preview`;
    return this.httpClient
      .get<ResponseDataInterfaces<IInstructorReassignmentPreviewRes>>(url, {
        params: {
          userId,
        },
      })
      .pipe(map(({ data }) => data));
  }

  public reassignPlanDevChief(reassignmentData: IReassignmentReq) {
    const url = `${this.headDevReassignmentUrl}/headDevelopment`;
    return this.httpClient
      .put<ResponseDataInterfaces<IHeadDevReassignmentRes>>(
        url,
        reassignmentData
      )
      .pipe(map(({ data }) => data));
  }

  public reassignPlanDevChiefPreview(id: number) {
    const url = `${this.headDevReassignmentUrl}/headDevelopment/preview/${id}`;
    return this.httpClient
      .get<ResponseDataInterfaces<IHeadDevReassignmentRes>>(url)
      .pipe(map(({ data }) => data));
  }

  public deletePlanningByID(planId: number) {
    const url = `${this.coursePlanURl}`;
    return this.httpClient.delete<ResponseDataInterfaces<boolean>>(
      `${url}/${planId}`
    );
  }

  public incrementPlanningValues(
    payload: IPostIncrementPlanValuesBody
  ): Observable<IIncreasedPlanValues[]> {
    const url = `${this.coursePlanURl}/incrementPlanningValues`;
    return this.httpClient
      .post<ResponseDataInterfaces<IPostIncrementPlanValuesResponse>>(
        url,
        payload
      )
      .pipe(map(({ data }) => data.valoresAumentados));
  }

  public duplicateAnnualPlanning(
    payload: IPostDuplicatePlanBody
  ): Observable<boolean> {
    const url = `${this.coursePlanURl}/duplicate`;
    return this.httpClient
      .post<ResponseDataInterfaces<boolean>>(url, payload)
      .pipe(map(({ data }) => data));
  }
}
